import React, { useRef, useState } from "react";

/**
 * Check if a URL points to a video file
 * @param {string} url - Media URL
 * @returns {boolean}
 */
export const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();

  return (
    videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
    lowerUrl.includes("/video/")
  );
};

/**
 * MediaDisplay component - Shows image or video based on URL
 * @param {Object} props
 * @param {string} props.src - Media source URL
 * @param {string} props.alt - Alt text for images
 * @param {Object} props.style - Custom styles
 * @param {string} props.className - CSS classes
 * @param {boolean} props.showPlayButton - Show play button overlay for videos
 * @param {boolean} props.autoPlayOnHover - Auto-play video on hover
 * @param {boolean} props.controls - Show video controls
 * @param {Function} props.onClick - Click handler
 */
const MediaDisplay = ({
  src,
  alt = "Media",
  style = {},
  className = "",
  showPlayButton = true,
  autoPlayOnHover = true,
  controls = false,
  onClick,
  ...props
}) => {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const isVideo = isVideoUrl(src);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (isVideo && autoPlayOnHover && videoRef.current) {
      videoRef.current.muted = false;
      setIsMuted(false);
      videoRef.current.play().catch(() => {
        // Autoplay with sound blocked, keep muted
        videoRef.current.muted = true;
        setIsMuted(true);
      });
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (isVideo && autoPlayOnHover && videoRef.current) {
      videoRef.current.muted = true;
      setIsMuted(true);
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  if (!src) {
    return null;
  }

  if (isVideo) {
    return (
      <div
        style={{ position: "relative", ...style }}
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        {...props}
      >
        <video
          ref={videoRef}
          src={src}
          muted={isMuted}
          loop
          playsInline
          controls={controls}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            ...style,
          }}
        />
        {showPlayButton && !isHovering && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "20px",
              pointerEvents: "none",
            }}
          >
            <i className="fa fa-play" style={{ marginLeft: "3px" }}></i>
          </div>
        )}
      </div>
    );
  }

  // Image
  return (
    <img
      src={src}
      alt={alt}
      style={style}
      className={className}
      onClick={onClick}
      {...props}
    />
  );
};

/**
 * MediaThumbnail - Small clickable thumbnail for gallery
 */
export const MediaThumbnail = ({
  src,
  isActive = false,
  onClick,
  style = {},
}) => {
  const isVideo = isVideoUrl(src);

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        width: "100%",
        height: "80px",
        cursor: "pointer",
        border: isActive ? "3px solid #00CC99" : "2px solid transparent",
        borderRadius: "8px",
        overflow: "hidden",
        transition: "border 0.3s",
        ...style,
      }}
    >
      {isVideo ? (
        <>
          <video
            src={src}
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
            }}
          >
            <i className="fa fa-play" style={{ marginLeft: "2px" }}></i>
          </div>
        </>
      ) : (
        <img
          src={src}
          alt="thumbnail"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
};

/**
 * MediaViewer - Full-size media viewer (image or video with controls)
 */
export const MediaViewer = ({
  src,
  alt = "Media",
  style = {},
  className = "",
}) => {
  const isVideo = isVideoUrl(src);

  if (!src) {
    return null;
  }

  if (isVideo) {
    return (
      <video
        src={src}
        controls
        autoPlay
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          borderRadius: "8px",
          ...style,
        }}
        className={className}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        ...style,
      }}
      className={`rounded-lg ${className}`}
    />
  );
};

export default MediaDisplay;
